import { getMarket, LoadingStates } from '@pb/utils'

import { ClubSetup } from 'App/duck/types'
import { RootState } from 'redux/types'

import { RootComponent, SubComponent } from '../PointsHistory'
import PointsHistoryState, {
  GroupedTransaction,
  Transaction,
  TransactionTypeAndPoints,
} from './types'

const getPointsHistoryPageState = (state: RootState): PointsHistoryState =>
  state.account.pointsHistoryLegacy

const getTotalNumerOfTransactions = (state: RootState): number =>
  getPointsHistoryPageState(state).totalNumberOfTransactions

const getResetOfPoints = (state: RootState): Transaction | undefined => {
  const { resetTransaction } = getPointsHistoryPageState(state)
  return resetTransaction
}
const isAnySubTypesActive = (activityCode: string, activitySubType: string[] = []): boolean =>
  activitySubType.includes(activityCode) || activitySubType[0]?.includes(activityCode)

const consciousPurchaseSubActivityType = ['ZGPS', 'ZGPO', 'ZGRS', 'ZGRO']
const purchaseSubActivityType = ['ZO01', 'ZP01', 'ZO02', 'ZP02']

const getTransactionsBasedOnActivitySubTypes = (
  transactions: Transaction[],
  subActivityTypes: string[],
): Transaction[] =>
  transactions.filter(({ activitySubType }) =>
    subActivityTypes.some((subtype) => isAnySubTypesActive(subtype, activitySubType)),
  )

const getTransactionsWithoutSubActivityTypes = (
  transactions: Transaction[],
  subActivityTypes: string[],
): Transaction[] =>
  transactions.filter(({ activitySubType }) =>
    subActivityTypes.every((subtype) => !isAnySubTypesActive(subtype, activitySubType)),
  )

const createObject = (
  activitySubType: string,
  allFoundTransactions: Transaction[],
): TransactionTypeAndPoints => ({
  activitySubType,
  points: allFoundTransactions.reduce((accumulator, { points }) => accumulator + points, 0),
})

type Order = 'otherPoints' | 'consciousPoints'
const getOrderOfPoints = (state: RootState, section: Order = 'consciousPoints'): string[] => {
  const market = getMarket()
  if (!market) {
    return []
  }
  const { pointsOverviewSectionOrders = {} } = getPointsHistoryPageState(state)
  const orderOfPoints =
    (pointsOverviewSectionOrders[market] && pointsOverviewSectionOrders[market][section]) || []

  return orderOfPoints.map((order) => order.toUpperCase())
}

const getIsConsciousPointsActive = (state: RootState): boolean => {
  const { pointsOverviewSectionOrders = {} } = getPointsHistoryPageState(state)
  const market = getMarket()

  return !!pointsOverviewSectionOrders[market]?.consciousPoints
}

const getPointsGroupedByTypes = (
  transactions: Transaction[],
  orderOfTypes: string[],
): TransactionTypeAndPoints[] =>
  orderOfTypes.map((activityType) => {
    const allTransactionForActivityType = getTransactionsBasedOnActivitySubTypes(transactions, [
      activityType,
    ])
    return createObject(activityType, allTransactionForActivityType)
  })

const getConsciousPoints = (state: RootState): TransactionTypeAndPoints[] => {
  if (!getIsConsciousPointsActive(state)) {
    return []
  }

  const { transactionsAfterReset } = getPointsHistoryPageState(state)

  const consciousPurchaseTransactions = getTransactionsBasedOnActivitySubTypes(
    transactionsAfterReset,
    consciousPurchaseSubActivityType,
  )

  const transactionsWithoutConsciousPoints = getTransactionsWithoutSubActivityTypes(
    transactionsAfterReset,
    consciousPurchaseSubActivityType,
  )
  const orderOfConsciousPoints = getOrderOfPoints(state)
  const consciousPoints = getPointsGroupedByTypes(
    transactionsWithoutConsciousPoints,
    orderOfConsciousPoints,
  )

  return [createObject('ZGPS', consciousPurchaseTransactions), ...consciousPoints]
}

const getTotalNumberOfConsciousPoints = (state: RootState): number => {
  const allConsciousPoints = getConsciousPoints(state)
  if (allConsciousPoints) {
    return allConsciousPoints.reduce((accumulator, { points }) => accumulator + points, 0)
  }
  return 0
}

interface ActivityTypeOtherPoints {
  transactions: Transaction[]
  orderOfOtherMemberPoints: string[]
  orderOfConsciousPoints: string[]
}

const getActivityTypeOtherPoints = ({
  transactions,
  orderOfOtherMemberPoints,
  orderOfConsciousPoints,
}: ActivityTypeOtherPoints): TransactionTypeAndPoints => {
  const transactionsWithoutConsciousPoints = transactions.filter(
    ({ activitySubType }) =>
      !activitySubType || activitySubType.every((ast) => !orderOfConsciousPoints.includes(ast)),
  )
  const otherPointsTransactions = transactionsWithoutConsciousPoints.filter(
    ({ activitySubType }) =>
      !activitySubType || activitySubType.some((ast) => !orderOfOtherMemberPoints.includes(ast)),
  )

  return createObject('other', otherPointsTransactions)
}

const getOtherMemberPoints = (state: RootState): TransactionTypeAndPoints[] => {
  const { transactionsAfterReset } = getPointsHistoryPageState(state)
  const isConsciousPointsActive = getIsConsciousPointsActive(state)
  const transactionsWithoutConsciousPurchase = isConsciousPointsActive
    ? getTransactionsWithoutSubActivityTypes(
        transactionsAfterReset,
        consciousPurchaseSubActivityType,
      )
    : transactionsAfterReset

  const purchaseTransaction = getTransactionsBasedOnActivitySubTypes(
    transactionsWithoutConsciousPurchase,
    purchaseSubActivityType,
  )
  const orderOfOtherMemberPoints = getOrderOfPoints(state, 'otherPoints')

  const transactionsWithoutPurchase = getTransactionsWithoutSubActivityTypes(
    transactionsWithoutConsciousPurchase,
    purchaseSubActivityType,
  )

  const activeOtherMemberPoints = getPointsGroupedByTypes(
    transactionsWithoutPurchase,
    orderOfOtherMemberPoints,
  )
  const orderOfConsciousPoints = getOrderOfPoints(state)

  const otherPoints = getActivityTypeOtherPoints({
    transactions: transactionsWithoutPurchase,
    orderOfOtherMemberPoints,
    orderOfConsciousPoints,
  })
  return [createObject('ZO01', purchaseTransaction), ...activeOtherMemberPoints, otherPoints]
}

const getTotalNumberOfOtherMemberPoints = (state: RootState): number => {
  const allOtherMemberPoints = getOtherMemberPoints(state)
  return allOtherMemberPoints.reduce((accumulator, { points }) => accumulator + points, 0)
}

const getSubComponentToShow = (state: RootState): SubComponent => {
  const totalNumberOfTransactions = getTotalNumerOfTransactions(state)
  if (!totalNumberOfTransactions) {
    return 'NoPointsMessage'
  }
  return 'PointsDetailsSection'
}

const getShouldFetchOrderOfPoints = (state: RootState): boolean => {
  const { loadingOrderStatus } = getPointsHistoryPageState(state)
  return (
    loadingOrderStatus === LoadingStates.NotLoaded ||
    loadingOrderStatus === LoadingStates.LoadedFail
  )
}

const getIsOrderOfPointsLoaded = (state: RootState): boolean => {
  const { loadingOrderStatus } = getPointsHistoryPageState(state)
  return (
    loadingOrderStatus === LoadingStates.LoadedSuccess ||
    loadingOrderStatus === LoadingStates.LoadedFail
  )
}

const getRootComponentToShow = (state: RootState): RootComponent => {
  const { loadingPointsHistoryStatus, loadingOrderStatus } = getPointsHistoryPageState(state)
  const isOrderOfPointsLoaded = getIsOrderOfPointsLoaded(state)
  const isLoadPointsHistory = loadingPointsHistoryStatus === LoadingStates.Loading

  if (isLoadPointsHistory || !isOrderOfPointsLoaded) {
    return 'Hide'
  }

  if (
    loadingPointsHistoryStatus === LoadingStates.LoadedFail ||
    loadingOrderStatus === LoadingStates.LoadedFail
  ) {
    return 'TechnicalErrorMessage'
  }

  return 'ShowPoints'
}

const sortTransactions = (
  { transactionDate: date1 }: GroupedTransactions | GroupedTransaction,
  { transactionDate: date2 }: GroupedTransactions | GroupedTransaction,
): number => {
  if (new Date(`${date1}`).valueOf() - new Date(`${date2}`).valueOf() > 0) {
    return -1
  }
  return 1
}

const sortGroupedTransactions = (
  groupedTransactions: GroupedTransactions[],
): GroupedTransactions[] => {
  const sortedTransactions = groupedTransactions.sort(sortTransactions)
  sortedTransactions.forEach(({ transactions = [] }) => transactions.sort(sortTransactions))
  return sortedTransactions
}

interface GroupedTransactions {
  transactionDate: string
  transactions: GroupedTransaction[]
}

const createGroupedTransactionsByDate = (transactions: Transaction[]): GroupedTransactions[] => {
  const groupedTransactions: Record<string, GroupedTransaction[]> = transactions.reduce(
    (accumulator, { transactionDateAndTime, activitySubType, correctionReasonText, ...data }) => {
      const date = transactionDateAndTime.split('T')[0]
      const transaction = {
        transactionDate: date,
        activitySubType: correctionReasonText ? [correctionReasonText] : activitySubType,
        ...data,
      }
      return {
        ...accumulator,
        [date]: [...(accumulator[date] || []), transaction],
      }
    },
    {},
  )
  const groupedTransactionsAsArray = Object.entries(groupedTransactions).map(([key, value]) => ({
    transactionDate: key,
    transactions: value,
  }))
  return sortGroupedTransactions(groupedTransactionsAsArray)
}

const getGroupedTransactionsByDateAfterReset = (state: RootState): GroupedTransactions[] => {
  const { transactionsAfterReset } = getPointsHistoryPageState(state)
  return createGroupedTransactionsByDate(transactionsAfterReset)
}

const getGroupedTransactionsByDateBeforeReset = (state: RootState): GroupedTransactions[] => {
  const { transactionsBeforeReset } = getPointsHistoryPageState(state)
  return createGroupedTransactionsByDate(transactionsBeforeReset)
}

const getAllConsciousPurchaseSubActivityType = (state: RootState): string[] => {
  if (!getIsConsciousPointsActive(state)) {
    return []
  }
  const consciousPoints = getOrderOfPoints(state)

  return [...consciousPurchaseSubActivityType, ...consciousPoints]
}

const getClubSetup = (state: RootState): ClubSetup => state.app.settings.clubSetup as ClubSetup

export default {
  getConsciousPoints,
  getTotalNumberOfConsciousPoints,
  getOtherMemberPoints,
  getTotalNumberOfOtherMemberPoints,
  getIsConsciousPointsActive,
  getGroupedTransactionsByDateAfterReset,
  getResetOfPoints,
  getGroupedTransactionsByDateBeforeReset,
  getRootComponentToShow,
  getSubComponentToShow,
  getIsOrderOfPointsLoaded,
  getShouldFetchOrderOfPoints,
  getAllConsciousPurchaseSubActivityType,
  getClubSetup,
}
